import { ApiConflictError, apiCall } from '@/lib/api-client';
import type { AgentDownloadFormat } from '@/features/agent-dispatch/download';
import type { PredicateSafety } from '@/features/predicate/result-types';

/**
 * FDA Submission Dossier run dispatch + polling API.
 *
 * Dispatch is multipart/form-data with up to 3 optional file uploads
 * (predicate_report, clinical_protocol, clinical_report) plus a
 * submission_type. Which uploads are required depends on submission_type,
 * gated client-side by the REQUIREMENTS map (built in Phase B). The tech
 * spec is NOT a dispatch upload — it comes from the product's on-file spec.
 *
 * The polling endpoint inlines the parsed `dossier_response` payload (the
 * structured dossier envelope: sections, predicate comparison, sources, …)
 * once status flips to "completed".
 */

export type AgentRunStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface DossierDispatchResponse {
  dossier_run_id: number;
  status: string;
  agent_kind: string;
  product_id: string;
  product_name: string;
  product_type: string;
  submission_type: string | null;
  poll_ms: number;
  max_polls: number;
}

/** Per-country override uploads (predicate report / CSP / CSR).
 *  Required for multi-country dispatch — the backend rejects shared
 *  per-country slot fields when `country.length > 1` so each child gets
 *  its own bytes without cross-country leakage.
 *  Keyed by uppercase ISO-2 country code (e.g. "US", "CA"). */
export interface PerCountryUploads {
  predicateReport?: File;
  clinicalProtocol?: File;
  clinicalReport?: File;
}

export interface DossierDispatchArgs {
  productId: number;
  submissionType: string;
  /** SHARED per-country slots — only honored when `country.length === 1`.
   *  In multi-country mode, send `perCountryUploads` instead; the backend
   *  rejects these shared fields with a 400 when `country.length > 1`. */
  predicateReport?: File;
  clinicalProtocol?: File;
  clinicalReport?: File;
  /** Per-country override uploads keyed by uppercase ISO-2 code. Each
   *  entry's files post as `<slot>_<COUNTRY>` multipart fields (e.g.
   *  `clinical_protocol_US`). Required for multi-country dispatch. */
  perCountryUploads?: Record<string, PerCountryUploads>;
  /** One or more country codes (e.g. ["US"], ["US","CA"]). Joined
   *  comma-separated on the wire. The backend now uses this to fan out
   *  into a multi-country dispatch (one run row per country). */
  country?: string[];
  /** EU-only metadata. Required when ``country`` includes ``"EU"``.
   *  Sent as a JSON-stringified form field; the backend stores it in
   *  each EU child run's ``input_summary`` so the (future) EU dossier
   *  agent module reads procedure + member states from there. */
  euMetadata?: import('./eu-member-states').EuDispatchMetadata;
  /** EU document type selector. Controls which Annex the EU dossier agent
   *  drafts. Defaults to `annex_ii_td` (Technical Documentation) when
   *  omitted. Ignored for non-EU runs. `sscp` is gated to
   *  `IIb_implantable` and `III` device classes (backend returns 400
   *  for other classes). `eu_ctd` is the medicinal (drug/biologic) CTD. */
  documentType?:
    | 'annex_ii_td'
    | 'annex_iii_pms'
    | 'sscp'
    | 'ivdr_td'
    | 'ivdr_pms'
    | 'ivdr_ssp'
    | 'eu_ctd'
    | 'ni_ivdr_td'
    | 'ni_ivdr_pms'
    | 'ni_ivdr_ssp';
  /** The legal entity (company) this dossier files AS. Posts as `company_id`.
   *  NULL → the backend falls back to the account's primary company. */
  companyId?: number;
  /** EU device "generate the whole dossier" toggle. When true on an EU-only
   *  device dispatch, one click fans out into the class-appropriate set of
   *  per-part runs (TD + PMS + SSCP/SSP) — each its own existing agent.
   *  Posts as `eu_complete`. Ignored for medicinal + US runs. */
  euComplete?: boolean;
  /** Per-country submission-type / EU metadata overrides for multi-country
   *  dispatch. When present, the function appends `submission_type_<C>`,
   *  `eu_metadata_<C>`, `document_type_<C>`, and `eu_complete_<C>` fields
   *  so each child run can receive a different pathway or EU document type.
   *  Keyed by uppercase ISO-2 country code (e.g. "US", "EU"). */
  perCountryPayloads?: Record<string, import('./dispatch/types').CountryPayload>;
  /** User-declared predicate K-number (US 510(k) family) — single-country
   *  mode only; posts as bare `user_predicate_k`. Multi-country dispatches
   *  carry it per country inside `perCountryPayloads` instead (the backend
   *  rejects the bare field in that mode, same rule as shared uploads). */
  userPredicateK?: string;
  /** The K-number the user confirmed in the MAUDE/recall warning (the 409 body's k_number).
   *  Posts as `predicate_safety_acknowledged_US=<K>` (accepted in single- and multi-country);
   *  the server accepts it only if it is still the predicate the dossier will use. */
  predicateSafetyAcknowledgedK?: string;
  /** On-file runs the user left out of THIS dossier: the server slots, keyed by
   *  backend country code. Posts as `exclude_on_file_<slot>_<C>=true` — always
   *  suffixed, single- or multi-country. */
  excludeOnFile?: Record<string, ExcludableOnFileSlot[]>;
}

/** Server names of the on-file inputs a dispatch may leave out of a dossier. */
export type ExcludableOnFileSlot = 'clinical_protocol' | 'clinical_report' | 'predicate_run';

/**
 * Dispatch a dossier run. Sends multipart/form-data with submission_type
 * plus any provided uploads. 409 conflict (in-flight run) surfaces as
 * `ApiConflictError` with `detail.existing_run_id`.
 *
 * Field naming:
 *  - `predicate_report` (etc.)  — shared per-country slots, single-country mode only.
 *  - `predicate_report_<CC>`    — per-country slots, multi-country mode (required).
 */
export async function dispatchDossierRun(
  args: DossierDispatchArgs,
): Promise<DossierDispatchResponse> {
  const form = new FormData();
  form.append('submission_type', args.submissionType);
  if (args.predicateReport) form.append('predicate_report', args.predicateReport);
  if (args.clinicalProtocol) form.append('clinical_protocol', args.clinicalProtocol);
  if (args.clinicalReport) form.append('clinical_report', args.clinicalReport);
  if (args.perCountryUploads) {
    for (const [country, slots] of Object.entries(args.perCountryUploads)) {
      if (slots.predicateReport) {
        form.append(`predicate_report_${country}`, slots.predicateReport);
      }
      if (slots.clinicalProtocol) {
        form.append(`clinical_protocol_${country}`, slots.clinicalProtocol);
      }
      if (slots.clinicalReport) {
        form.append(`clinical_report_${country}`, slots.clinicalReport);
      }
    }
  }
  if (args.country && args.country.length > 0) form.append('country', args.country.join(','));
  if (args.companyId != null) form.append('company_id', String(args.companyId));
  if (args.euMetadata) form.append('eu_metadata', JSON.stringify(args.euMetadata));
  // Always send document_type for EU runs; backend ignores it for non-EU.
  // Default is annex_ii_td so omitting from the form sends the safe default.
  if (args.documentType) form.append('document_type', args.documentType);
  // EU device: generate the whole class-appropriate dossier in one dispatch.
  if (args.euComplete) form.append('eu_complete', 'true');
  // User-declared predicate (US 510(k) family), single-country mode.
  if (args.userPredicateK) form.append('user_predicate_k', args.userPredicateK);
  if (args.predicateSafetyAcknowledgedK) {
    form.append('predicate_safety_acknowledged_US', args.predicateSafetyAcknowledgedK);
  }
  // Per-country overrides for multi-country dispatch. Each entry appends
  // suffixed fields (`submission_type_<C>`, etc.) so the backend can assign
  // a different pathway / document type to each child run. The bare fields
  // above remain in place — the backend prefers the suffixed value per country.
  if (args.perCountryPayloads) {
    for (const [c, p] of Object.entries(args.perCountryPayloads)) {
      form.append(`submission_type_${c}`, p.submissionType);
      if (p.euMetadata) form.append(`eu_metadata_${c}`, JSON.stringify(p.euMetadata));
      if (p.documentType) form.append(`document_type_${c}`, p.documentType);
      if (p.euComplete) form.append(`eu_complete_${c}`, 'true');
      if (p.userPredicateK) form.append(`user_predicate_k_${c}`, p.userPredicateK);
    }
  }
  if (args.excludeOnFile) {
    for (const [c, slots] of Object.entries(args.excludeOnFile)) {
      for (const slot of slots) form.append(`exclude_on_file_${slot}_${c}`, 'true');
    }
  }
  return apiCall<DossierDispatchResponse>(
    `/api/products/${args.productId}/dossier`,
    { method: 'POST', bodyRaw: form },
  );
}

export function existingDossierRunIdFromConflict(err: unknown): number | null {
  if (!(err instanceof ApiConflictError)) return null;
  const detail = err.detail as { detail?: { existing_run_id?: unknown } } | undefined;
  const id = detail?.detail?.existing_run_id;
  return typeof id === 'number' ? id : null;
}

/** Log a Cancel on the MAUDE/recall warning for a typed-in K-number. */
export async function cancelDeclaredPredicateSafety(
  productId: number,
  kNumber: string,
): Promise<{ message: string }> {
  return apiCall<{ message: string }>(
    `/api/products/${productId}/dossier/predicate-safety-cancel`,
    { method: 'POST', json: { k_number: kNumber } },
  );
}

/** Traceability provenance manifest, frozen at completion. Pointers + hashes
 *  only — never document content. NULL on legacy / in-flight runs. */
export interface ProvenanceSource {
  source_id: string;
  kind: string;
  label: string;
  ref: string;
  sha256: string | null;
  version: string | null;
  origin: string | null;
  /** Human detail (e.g. a predicate's verified K-number(s) + clearance). */
  detail: string | null;
}

export interface ProvenanceSectionLink {
  section_key: string;
  source_id: string;
  /** `exact` = code-assembled (provable). `contextual` = generated with the
   *  source in scope (honest — never a fabricated page/passage). */
  tier: 'exact' | 'contextual';
}

/** A value-level provable row: a specific value in the dossier and the exact
 *  source field it came from. */
export interface ProvenanceClaim {
  section_key: string;
  element: string;
  value: string;
  source_id: string;
  source_field: string;
  tier: 'exact' | 'contextual';
  /** Tier 2: page locator (e.g. `CSP p.1 (+24 more) · CSR p.3`) when the value
   *  was verified literally present in the source document(s). Absent for
   *  Tier-1 structured claims. */
  locator?: string | null;
  /** Tier 2: PHI-safe nearest section heading at the primary hit (structural
   *  title only — never patient text). */
  context?: string | null;
  /** Reconciliation: when a field matched via a distinctive phrase (not the
   *  whole value), the phrase that matched — e.g. 'matched phrase: "…"'. */
  note?: string | null;
}

/** Country-anchored regulatory framing (US → FDA RTM; EU → GSPR matrix). */
export interface ProvenanceFramework {
  id: string;
  label: string;
  matrix_name: string;
  authority: string;
  market: string | null;
  /** Drug/biologic (CTD) runs: what the verified finder pick is called ("FDA-verified
   *  reference"). Absent on device runs, which say "FDA-verified predicate". */
  reference_label?: string;
}

export interface ProvenanceCoverage {
  sections_total: number;
  sections_traced: number;
  traced: string[];
  gaps: string[];
}

/** A2: statement-level tally — of the substantive sentences, how many are
 *  directly traced to a source vs flagged as unsourced. */
export interface StatementCoverage {
  total: number;
  traced: number;
  flagged: number;
  /** directly-traced / total substantive statements (0–1), or null if empty. */
  score: number | null;
}

export interface ProvenanceRunMeta {
  run_id?: number;
  submission_type?: string | null;
  is_ivd?: boolean;
  country?: string | null;
  market?: string | null;
  template_key?: string | null;
  model_id?: string | null;
  generated_at?: string | null;
  actor_email?: string | null;
  content_sha256?: string | null;
  [k: string]: unknown;
}

/** Regulatory-substance verification — cited claims checked against authoritative
 *  sources (FDA Recognized Standards DB, eCFR). Verdicts come from the authority,
 *  never the model. A non-pass is a finding, not an error. */
/** A source quote + where in the dossier it was taken from. */
export interface SourceQuote {
  text: string;
  /** The dossier section the quote came from, e.g. `section_05_consensus_standards`. */
  in: string;
}

export interface DefenseStandard {
  designation: string;
  cited_in: string[];
  /** One source quote per place the standard is cited (RA-requested). */
  quotes?: SourceQuote[];
  recognition_number: string | null;
  /** recognized = FDA lists it (its recognized edition is in `note`, for the
   *  human to compare) · review = no FDA entry (QMS/lab std, superseded, or a
   *  cite to tighten) · unverified = DB unreachable. For the EU, 'unverified'
   *  means a verified "not harmonised" (see `label`), not a failure to check.
   *  recognised_international = AU: cited recognised international standard
   *  (TGA has no single designated-standards register; informational, not pass/fail).
   *  designated = UK: confirmed on the gov.uk designated-standards register ✓.
   *  not_designated = UK: checked and not on the register (definite result, not a failure). */
  status: 'recognized' | 'review' | 'unverified' | 'recognised_international' | 'designated' | 'not_designated';
  /** Optional badge text override (EU: "harmonised" / "not harmonised"). When
   *  absent the badge falls back to the status word. */
  label?: string;
  source: string | null;
  /** FDA's recognized edition, straight from the DB — e.g.
   *  "ISO 14971 Third Edition 2019-12". Lets a human spot an edition mismatch. */
  note: string | null;
  /** Click-through to the FDA Recognized Standards record. */
  url: string | null;
}

export interface DefenseRegulation {
  /** US/eCFR rows: the CFR citation. EU rows use `designation` instead. */
  cfr?: string;
  /** EU legislation rows: the cited act (e.g. "Regulation (EC) No 726/2004"). */
  designation?: string;
  /** Regulation title from eCFR — e.g. "Quality System Regulation". */
  name: string | null;
  /** EU: the verification note (in-scope / REPEALED — superseded by X). */
  note?: string | null;
  /** EU: short status label (e.g. "EU legislation" / "repealed — superseded"). */
  label?: string | null;
  cited_in: string[];
  /** One source quote per place the CFR is cited (RA-requested). */
  quotes?: SourceQuote[];
  /** verified = eCFR/eCTD confirmed · registered = AU FRL confirmed (in-force) ·
   *  recognized = EU legislation in scope · repealed = act superseded ·
   *  review = cited but absent from the authority register · unverified = unreachable. */
  status: 'verified' | 'review' | 'unverified' | 'recognized' | 'repealed' | 'registered';
  source: string | null;
  /** Click-through to the eCFR section / EUR-Lex record. */
  url: string | null;
  /** A1: set when this reg belongs to a DIFFERENT submission pathway. */
  pathway_flag?: boolean;
}

/** A5: a predicate device surfaced in the verification block (finder-verified). */
export interface DefensePredicate {
  k_number: string;
  detail: string;
  status: 'verified' | 'review' | 'unverified';
  source: string | null;
  url: string | null;
}

/** A1: pathway-appropriate CFR check. */
export interface DefensePathway {
  submission_type: string | null;
  /** The CFR part(s) this pathway should cite — e.g. ["21 CFR 601"]. */
  expected: string[];
  expected_cited: boolean;
  findings: { cfr: string; note: string }[];
  status: 'ok' | 'review';
}

export interface DefenseTally {
  total: number;
  verified: number;
  flagged: number;
  unverified: number;
}

/** CA-only: the dossier's cited Schedule 1 classification, checked against the
 *  structured rule set — is the cited rule real and the class a valid outcome? */
export interface DefenseClassification {
  part: string | null;
  rule: number | null;
  status: 'verified' | 'class_mismatch' | 'invalid_rule' | 'unparsed';
  valid_classes?: string[];
  note: string;
}

/** EU medicinal: the declared legal basis, checked for recognition + product-family
 *  validity (a biosimilar basis requires a biologic, etc.) — the CTD analog of CA's
 *  classification check. */
export interface DefenseLegalBasis {
  basis: string | null;
  label?: string | null;
  family?: string | null;
  product_type?: string | null;
  status: 'verified' | 'family_mismatch' | 'unrecognized';
  note: string;
}

export interface ProvenanceDefense {
  /** The verifying authority — "US FDA", or null when not yet wired. */
  authority: string | null;
  /** False for jurisdictions whose authoritative sources aren't wired yet. */
  available: boolean;
  standards: DefenseStandard[];
  regulations: DefenseRegulation[];
  predicates: DefensePredicate[];
  pathway: DefensePathway | null;
  /** Optional: CA dossiers carry a Schedule 1 classification check. */
  classification?: DefenseClassification | null;
  /** Optional: EU medicinal dossiers carry a legal-basis validity check. */
  legal_basis?: DefenseLegalBasis | null;
  summary: {
    standards: DefenseTally;
    regulations: DefenseTally;
    predicates: DefenseTally;
  };
}

/** A substantive dossier sentence that makes a specific factual claim but
 *  traces to NO verifiable source — the reviewer's "says who?" worklist. */
export interface UnsourcedStatement {
  section_key: string;
  value: string;
  /** The factual value that made it a claim (e.g. "98.2%", "190 mg"). */
  contains: string;
}

/** USA 510(k)-exempt ONLY: one non-blocking review row from
 *  ``app.dossier.usa.exempt_grounding.verify_510k_exempt_determinations`` —
 *  a determination-grounding check the model's ``exemption_outcome`` and
 *  supporting prose were cross-checked against (FDA classification record /
 *  prose-completeness signal). */
export interface ExemptGroundingRow {
  /** e.g. `threshold_test`, `named_comparator`, `rx_otc_determination`,
   *  `summary_malfunction`, `exemption_grounding`. */
  key: string;
  status: 'verified' | 'review' | 'unverified';
  detail: string;
}

export interface ProvenanceManifest {
  schema_version: number;
  framework: ProvenanceFramework | null;
  run: ProvenanceRunMeta;
  sources: ProvenanceSource[];
  claims: ProvenanceClaim[];
  section_links: ProvenanceSectionLink[];
  coverage: ProvenanceCoverage | null;
  /** Optional: absent on legacy manifests. */
  statement_coverage?: StatementCoverage | null;
  defense: ProvenanceDefense | null;
  /** Optional: absent on legacy manifests. */
  unsourced?: UnsourcedStatement[];
  /** Optional: USA 510(k)-exempt ONLY. Absent for every other submission
   *  type / country and for legacy manifests. */
  exempt_grounding?: ExemptGroundingRow[];
}

/** The post-run stages a running dossier reports (backend `app/dossier/passes.StageLabel`). */
export type DossierRunStage = 'checking' | 'editing' | 'finishing';

export interface DossierRunDetail {
  id: number;
  /** Customer-facing "Run #N" — per (product, agent). Null on legacy pre-backfill rows. */
  run_number?: number | null;
  product_id: number;
  /** Plucked out of ``Product.data["product_name"]`` by the GET handler
   *  so the result-page meta strip can label which product the run is
   *  for without a second fetch. ``null`` when the product row has no
   *  ``product_name`` key. */
  product_name: string | null;
  /** Display name of the operator who ran the dossier ("generated by"),
   *  resolved server-side from created_by_user_id. Null on missing user. */
  created_by_name: string | null;
  agent_kind: string;
  status: AgentRunStatus;
  submission_type: string | null;
  /** EU annex this run produced (annex_ii_td / ivdr_pms / sscp / …). NULL for
   *  US runs + legacy. Used to label annex sections in the grouped EU tab. */
  document_type?: string | null;
  // Non-null when this run is part of a multi-country chain dispatch.
  // Result page reads this to auto-promote to the tabbed multi-country
  // view. NULL on legacy single-country runs.
  country: string | null;
  dispatch_group_id: string | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  /** The post-run stage while the run is running (`editing` while the editor pass runs), from
   *  the saved draft. Null when the run is not running or no AI pass has started. */
  stage: DossierRunStage | null;
  dossier_response: DossierResponse | null;
  output_metadata: Record<string, unknown> | null;
  /** Traceability manifest (sources + section links), frozen at completion.
   *  NULL on legacy / in-flight runs. Rendered as the Traceability Matrix. */
  provenance: ProvenanceManifest | null;
  poll_ms: number;
  max_polls: number;
}

/** A bibliography entry as the PDF prints it; a missing part is null. */
export interface EctdSourceEntry {
  title: string | null;
  identifier: string | null;
  url: string | null;
  note: string | null;
}

/**
 * What the results page shows of a U.S. drug / biologic eCTD's front matter (title,
 * bibliography, closing disclaimer). The response also carries the cover rows, CTOC line and
 * Document Control; they are download-only (PDF and Word) and the page ignores them. Built by the backend (`ectd_front_matter`, model
 * `EctdFrontMatter` in `backend/schemas.py`) from the same functions the PDF and the Word file
 * read, so the page shows exactly that. Added to the run-detail response for the five U.S.
 * medicinal submission types only. `components/ectd-front-matter.fixture.json` is a real one, held
 * equal to the backend's output by `tests/test_dossier_results_parity.py`.
 */
export interface EctdFrontMatter {
  title: string;
  bibliography: { title: string; entries: EctdSourceEntry[] };
  disclaimer: { lead: string; text: string };
}

/**
 * Top-level dossier response envelope from `execute_dossier_run`.
 *
 * The structured payload (sections, predicate_comparison, sources, eSTAR
 * fields, …) is intentionally loose-typed here — the renderer (Phase C)
 * iterates the named slots with bespoke layouts plus a humanize-key
 * fallback for anything not explicitly handled.
 */
export interface DossierResponse {
  product_id: string;
  product_name: string;
  product_type: string;
  submission_type: string | null;
  /** Whether this device routed to the IVD eSTAR template (vs non-IVD).
   *  Resolved server-side from the FDA classification; the result renderer
   *  will pick the IVD vs non-IVD layout off this (Phase 3). */
  is_ivd?: boolean;
  model: string;
  iterations: number;
  dossier: Record<string, unknown>;
  /** Cover-letter / applicant fields the agent files with (company, FEI,
   *  signatory, contact). A SIBLING of `dossier`, not nested under it. */
  submission_header?: Record<string, unknown>;
  /** U.S. medicinal eCTD runs only: the title, bibliography and disclaimer. */
  ectd_front_matter?: EctdFrontMatter | null;
  /** The file the run's download button gives: the Word file (the one Vault opens on checkout)
   *  for dossiers whose country prints Word, else the frozen PDF. Absent on older responses. */
  download_format?: DossierDownloadFormat;
  tool_calls: DossierToolCall[];
  warnings: string[];
  dossier_run_id: number;
  [extra: string]: unknown;
}

export interface DossierToolCall {
  tool: string;
  arguments: Record<string, unknown>;
  ok: boolean;
  summary?: string | null;
}

/** Reflect-back preview for a user-typed predicate K-number: what the number
 *  actually resolves to (device, original applicant, clearance date) plus a
 *  product-code comparison against this product's captured FDA code.
 *  `found: false` is a normal answer (unknown K), not an error. */
export interface PredicateLookup {
  found: boolean;
  k_number: string;
  device_name: string | null;
  applicant: string | null;
  decision_date: string | null;
  record_product_code: string | null;
  product_code_match: boolean | null;
  safety?: PredicateSafety | null;
}

export async function lookupPredicateK(
  productId: number,
  k: string,
): Promise<PredicateLookup> {
  return apiCall<PredicateLookup>(
    `/api/products/${productId}/predicate-lookup?k=${encodeURIComponent(k)}`,
  );
}

/** EU twin of PredicateLookup. `found: false` is a normal answer, not an error. */
export interface ComparatorLookup {
  found: boolean;
  basic_udi: string;
  device_name: string | null;
  company: string | null;
  manufacturer_srn: string | null;
  risk_class: string | null;
  emdn_code: string | null;
  /** Devices sharing this Basic UDI-DI (a device GROUP). `> 1` means
   *  device_name is one member's, not the group's. */
  group_size?: number | null;
}

export async function lookupComparatorUdi(
  productId: number,
  udi: string,
): Promise<ComparatorLookup> {
  return apiCall<ComparatorLookup>(
    `/api/products/${productId}/comparator-lookup?udi=${encodeURIComponent(udi)}`,
  );
}

export async function getDossierRun(runId: number): Promise<DossierRunDetail> {
  return apiCall<DossierRunDetail>(`/api/agents/dossier/runs/${runId}`, {
    method: 'GET',
  });
}

/** One per-country run inside a multi-country dossier dispatch.
 *  Mirrors the predicate-side ``DispatchChildRun`` shape. */
export interface DossierDispatchChildRun {
  id: number;
  country: string | null;
  status: AgentRunStatus;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
}

/** Returned from GET /api/agents/dossier/dispatch/{group_id}. */
export interface DossierDispatchDetailResponse {
  dispatch_group_id: string;
  runs: DossierDispatchChildRun[];
}

/** Fetch every sibling run in a dossier dispatch group. Polled while at
 *  least one sibling is queued/running. */
export async function getDossierDispatch(
  groupId: string,
): Promise<DossierDispatchDetailResponse> {
  return apiCall<DossierDispatchDetailResponse>(
    `/api/agents/dossier/dispatch/${groupId}`,
    { method: 'GET' },
  );
}

// ── EU MDR SSCP document type ─────────────────────────────────────────────────

/** §1 sub-object of the SSCP document (MDR Art. 32 / MDCG 2019-9). */
export interface SscpSection1 {
  device_identification: string | null;
  intended_purpose: string | null;
  indications: string | null;
  contraindications: string | null;
  target_patient_population: string | null;
}

/**
 * Top-level shape of the EU MDR SSCP dossier object, as produced by
 * `eu_sscp_renderer` and validated by `schema_sscp.SscpDocument`.
 *
 * Fields set to `__PLACEHOLDER__` (or `__PLACEHOLDER__:<hint>`) by the
 * agent must be surfaced via the shared `Placeholder` primitive.
 *
 * Gated to `IIb_implantable` and `III` device classes; the backend returns
 * 400 for any other class.
 */
export interface SscpDocument {
  device_class: 'IIa' | 'IIb' | 'IIb_implantable' | 'III';
  basic_udi_di: string | null;
  sscp_revision: string | null;
  s1_device_and_intended_purpose: SscpSection1;
  s2_device_description: string | null;
  s3_diagnostic_or_therapeutic_alternatives: string | null;
  s4_reference_to_harmonised_standards_and_cs: string | null;
  s5_clinical_evaluation_summary: string | null;
  s6_suggested_profile_and_training_for_users: string | null;
  s7_residual_risks_undesirable_effects_warnings_precautions: string | null;
  warnings: string[];
}

// ── EU MDR Conformity Route ───────────────────────────────────────────────────

/** One conformity assessment route (primary or alternative). */
export interface ConformityRoute {
  basis: string;
  td_assessment_basis: string;
  exemptions: string[];
}

/** One Notified Body consultation entry. */
export interface NbConsultation {
  name: string;
  applies_when: string;
  description: string;
}

/** Response shape from GET /api/products/{id}/conformity-route?device_class=<…>. */
export interface ConformityRouteResult {
  device_class: string;
  primary_route: ConformityRoute;
  alternative_route: ConformityRoute;
  nb_consultations: NbConsultation[];
  output_chain: string[];
  notes: string[];
}

/**
 * Fetch the conformity assessment route summary for a product and EU MDR
 * device class. Calls GET /api/products/{productId}/conformity-route
 * with `device_class` as a query parameter.
 *
 * @param productId  The product's numeric ID.
 * @param deviceClass  EU MDR device class string (e.g. "IIa", "IIb", "III").
 */
export async function fetchConformityRoute(
  productId: number,
  deviceClass: string,
): Promise<ConformityRouteResult> {
  return apiCall<ConformityRouteResult>(
    `/api/products/${productId}/conformity-route?device_class=${encodeURIComponent(deviceClass)}`,
    { method: 'GET' },
  );
}

// ── EU MDR Clinical Requirements ─────────────────────────────────────────────

/** One EU MDR clinical requirement entry. */
export interface ClinicalRequirement {
  name: string;
  applicability: string;
  basis: string;
  notes?: string;
}

/** Response shape from GET /api/products/{id}/clinical-requirements?device_class=<…>. */
export interface ClinicalRequirementsResult {
  device_class: string;
  requirements: ClinicalRequirement[];
  notes: string[];
}

/**
 * Fetch the clinical requirements summary for a product and EU MDR device
 * class. Calls GET /api/products/{productId}/clinical-requirements with
 * `device_class` as a query parameter.
 *
 * @param productId   The product's numeric ID.
 * @param deviceClass EU MDR device class string (e.g. "IIa", "IIb", "III").
 */
export async function fetchClinicalRequirements(
  productId: number,
  deviceClass: string,
): Promise<ClinicalRequirementsResult> {
  return apiCall<ClinicalRequirementsResult>(
    `/api/products/${productId}/clinical-requirements?device_class=${encodeURIComponent(deviceClass)}`,
    { method: 'GET' },
  );
}

// ── EU MDR Declaration of Conformity ─────────────────────────────────────────

export interface DocManufacturer {
  name: string | null;
  srn: string | null;
  address: string | null;
  authorised_representative: string | null;
}

export interface DocProductId {
  product_name: string | null;
  trade_name: string | null;
  product_code: string | null;
}

export interface DocNotifiedBody {
  name: string | null;
  identification_number: string | null;
  certificate_reference: string | null;
  conformity_assessment_route: string;
}

export interface DocSignatory {
  place: string | null;
  date: string | null;
  name: string | null;
  function: string | null;
  on_behalf_of: string | null;
}

/** Response shape from GET /api/products/{id}/declaration-of-conformity?device_class=<…>. */
export interface DeclarationOfConformity {
  device_class: string;
  manufacturer: DocManufacturer;
  sole_responsibility_statement: string;
  basic_udi_di: string | null;
  product_identification: DocProductId;
  risk_class_statement: string;
  conformity_statement: string;
  standards_and_cs_applied: string[];
  notified_body: DocNotifiedBody;
  additional_information: string | null;
  signatory: DocSignatory;
  notes: string[];
}

/**
 * Fetch the Declaration of Conformity for a product and EU MDR device class.
 * Calls GET /api/products/{productId}/declaration-of-conformity with
 * `device_class` as a query parameter.
 *
 * @param productId   The product's numeric ID.
 * @param deviceClass EU MDR device class string (e.g. "IIa", "IIb", "III").
 */
export async function fetchDeclarationOfConformity(
  productId: number,
  deviceClass: string,
): Promise<DeclarationOfConformity> {
  return apiCall<DeclarationOfConformity>(
    `/api/products/${productId}/declaration-of-conformity?device_class=${encodeURIComponent(deviceClass)}`,
    { method: 'GET' },
  );
}

// ── EU MDR UDI / EUDAMED Data ─────────────────────────────────────────────────

/** One special attribute entry in the UDI / EUDAMED data set. */
export interface UdiAttribute {
  name: string;
  value: string | null;
}

/** Response shape from GET /api/products/{id}/udi-eudamed-data?device_class=<…>. */
/** One UDI-DI and what it identifies (Annex VI Part C). */
export interface UdiDiEntry {
  udi_di: string;
  configuration: string;
  packaging_level: string;
  /** The device model it belongs to — named when the product has several Basic UDI-DIs. */
  basic_udi_di?: string;
}

export interface UdiEudamedData {
  device_class: string;
  basic_udi_di: string | null;
  /** One per device model; with several, each UDI-DI row names its own. */
  basic_udi_dis?: string[];
  /** The device's EUDAMED registration, as a statement (RA B.4); null → to-do. */
  eudamed_registration?: string | null;
  /** GS1 / HIBCC / ICCBBA / IFA — whose format the identifiers follow (RA B.2). */
  issuing_entity?: string | null;
  udi_di: string | null;
  /** One row per UDI-DI when the product lists them; otherwise `udi_di` stands. */
  udi_dis?: UdiDiEntry[];
  device_name: string | null;
  model_reference: string | null;
  manufacturer_srn: string | null;
  risk_class: string;
  emdn_code: string | null;
  special_attributes: UdiAttribute[];
  notes: string[];
}

/**
 * Fetch the UDI / EUDAMED data set for a product and EU MDR device class.
 * Calls GET /api/products/{productId}/udi-eudamed-data with
 * `device_class` as a query parameter.
 *
 * @param productId   The product's numeric ID.
 * @param deviceClass EU MDR device class string (e.g. "IIa", "IIb", "III").
 */
export async function fetchUdiEudamedData(
  productId: number,
  deviceClass: string,
): Promise<UdiEudamedData> {
  return apiCall<UdiEudamedData>(
    `/api/products/${productId}/udi-eudamed-data?device_class=${encodeURIComponent(deviceClass)}`,
    { method: 'GET' },
  );
}

// ── EU MDR Combined Dossier (all 7 parts) ─────────────────────────────────────

/** What the complete EU / NI MDR dossier's cover states (backend `CombinedIdentity`); null = not
 *  on file. */
export interface CombinedIdentity {
  manufacturer: string | null;
  /** The device's Basic UDI-DI, from its Countries & Regulatory Classification (RA B.1). */
  basic_udi_di?: string | null;
  /** The device's EUDAMED registration (RA B.4), from the product. */
  eudamed_registration?: string | null;
  manufacturer_address: string | null;
  srn: string | null;
  authorised_representative: string | null;
  /** False for a manufacturer established in the EU (Art 11(1)): no representative, mandate or rep PRRC. */
  authorised_representative_applies?: boolean;
  prrc: string | null;
  prrc_qualification_evidence?: string | null;
  /** Stated only by a micro / small enterprise (Art. 15(2)). */
  prrc_arrangement?: string | null;
  ec_rep_mandate?: string | null;
  ec_rep_prrc?: string | null;
  notified_body: string | null;
  conformity_route: string | null;
  device_status: string | null;
  /** Article 53(1) statement from the dispatch answer; may carry a placeholder for missing details. */
  other_nb_applications?: string | null;
  /** Annex IX Section 5 consultations at a glance (from the TD). */
  consultation_procedures?: string | null;
}

/**
 * All parts of a combined EU MDR or IVDR dossier, as returned by
 * GET /api/products/{id}/eu-dossier?format=json (or /ivdr-dossier).
 *
 * Mirrors the Pydantic `CombinedParts` (MDR) and `IvdrCombinedParts` (IVDR)
 * models in backend/services/dossier/eu_combined_renderer/__init__.py and
 * eu_ivdr_combined_renderer/__init__.py.
 *
 * Agent-generated parts (td, pms, sscp, …) are null when no completed run
 * exists yet. Deterministic parts (conformity_route, gspr_matrix, psur, …)
 * are assembled from product/company data and are always present.
 */
export interface CombinedEuParts {
  device_class: string;
  device_name?: string | null;
  manufacturer?: string | null;
  identity?: CombinedIdentity | null;
  td?: Record<string, unknown> | null;
  pms?: Record<string, unknown> | null;
  sscp?: Record<string, unknown> | null;
  conformity_route?: Record<string, unknown> | null;
  clinical_requirements?: Record<string, unknown> | null;
  declaration_of_conformity?: Record<string, unknown> | null;
  udi_eudamed_data?: Record<string, unknown> | null;
  references?: string[];
  gspr_matrix?: Record<string, unknown> | null;
  psur?: Record<string, unknown> | null;
  implant_card?: Record<string, unknown> | null;
  sscp_lay?: Record<string, unknown> | null;
  // IVDR adds: ssp, performance_evaluation (optional here; handled later)
  ssp?: Record<string, unknown> | null;
  performance_evaluation?: Record<string, unknown> | null;
}

/** `&run_id=<n>` pins a complete dossier to ONE dispatch (any of its runs); null leaves it out,
 *  and the backend uses the product's latest completed dispatch (spec 2026-10-23 B). */
function runIdParam(runId: number | null): string {
  return runId === null ? '' : `&run_id=${runId}`;
}

/**
 * The complete EU MDR dossier URL: GET /api/products/{productId}/eu-dossier?device_class=<…>&format=…
 * Single-sourced here so callers import it rather than building the URL themselves.
 */
export function combinedEuDossierUrl(
  productId: number,
  deviceClass: string,
  format: 'pdf' | 'html' | 'json' = 'pdf',
  runId: number | null = null,
): string {
  return `/api/products/${productId}/eu-dossier?device_class=${encodeURIComponent(deviceClass)}&format=${format}${runIdParam(runId)}`;
}

/**
 * Build the URL for the combined EU IVDR dossier download endpoint.
 * GET /api/products/{productId}/ivdr-dossier?device_class=<…>&format=pdf
 *
 * Sibling of {@link combinedEuDossierUrl} (MDR). IVDR device classes are
 * A / A_sterile / B / C / D.
 */
export function combinedIvdrDossierUrl(
  productId: number,
  deviceClass: string,
  format: 'pdf' | 'html' | 'json' = 'pdf',
  runId: number | null = null,
): string {
  return `/api/products/${productId}/ivdr-dossier?device_class=${encodeURIComponent(deviceClass)}&format=${format}${runIdParam(runId)}`;
}

/**
 * Build the URL for the combined NI IVDR dossier download endpoint.
 * GET /api/products/{productId}/ni-ivdr-dossier?device_class=<…>&format=json|pdf|html
 *
 * Sibling of {@link combinedIvdrDossierUrl} (EU IVDR), but assembled from the
 * Northern Ireland (CE-UKNI) runs rather than the EU ones. NI is always IVDR;
 * device classes are A / A_sterile / B / C / D.
 */
export function combinedNiIvdrDossierUrl(
  productId: number,
  deviceClass: string,
  format: 'pdf' | 'html' | 'json' = 'pdf',
  runId: number | null = null,
): string {
  return `/api/products/${productId}/ni-ivdr-dossier?device_class=${encodeURIComponent(deviceClass)}&format=${format}${runIdParam(runId)}`;
}

/**
 * Build the URL for the combined Northern Ireland (Windsor) MDR dossier endpoint.
 * GET /api/products/{productId}/ni-dossier?device_class=<…>&format=json|pdf|html
 *
 * Sibling of {@link combinedEuDossierUrl} (EU MDR): identical MDR document set +
 * renderer, but assembled from the product's NI (CE-UKNI) runs. Device classes
 * are the MDR set IIa / IIb / IIb_implantable / III.
 */
export function combinedNiMdrDossierUrl(
  productId: number,
  deviceClass: string,
  format: 'pdf' | 'html' | 'json' = 'pdf',
  runId: number | null = null,
): string {
  return `/api/products/${productId}/ni-dossier?device_class=${encodeURIComponent(deviceClass)}&format=${format}${runIdParam(runId)}`;
}

/** Which complete device dossier: EU MDR, EU IVDR, Northern Ireland MDR or NI IVDR. */
export type CombinedDeviceDossierKind = 'eu' | 'ivdr' | 'ni' | 'ni-ivdr';

const COMBINED_DEVICE_DOSSIER_URL: Record<
  CombinedDeviceDossierKind,
  (productId: number, deviceClass: string, format: 'pdf' | 'html' | 'json', runId: number | null) => string
> = {
  eu: combinedEuDossierUrl,
  ivdr: combinedIvdrDossierUrl,
  ni: combinedNiMdrDossierUrl,
  'ni-ivdr': combinedNiIvdrDossierUrl,
};

export type DossierDownloadFormat = AgentDownloadFormat;

/** One run's download: the Word file when the server says the dossier prints Word, else the
 *  frozen PDF. ``/download`` picks the file server-side, so a caller without the format (a list
 *  row) can use it too. */
export function dossierRunDownloadUrl(runId: number, format: DossierDownloadFormat | null = null): string {
  return format === 'pdf'
    ? `/api/agents/dossier/runs/${runId}/pdf`
    : `/api/agents/dossier/runs/${runId}/download`;
}

/** The URL of a complete device dossier, in any format (the page's Download PDF uses it).
 *  ``runId`` pins it to one dispatch; null = the latest completed dispatch. */
export function combinedDeviceDossierUrl(
  kind: CombinedDeviceDossierKind,
  productId: number,
  deviceClass: string,
  format: 'pdf' | 'html' | 'json' = 'pdf',
  runId: number | null = null,
): string {
  return COMBINED_DEVICE_DOSSIER_URL[kind](productId, deviceClass, format, runId);
}

/** A complete device dossier's parts (format=json) — what the complete-dossier page renders. */
export async function getCombinedDeviceDossier(
  kind: CombinedDeviceDossierKind,
  productId: number,
  deviceClass: string,
  runId: number | null,
): Promise<CombinedEuParts> {
  return apiCall<CombinedEuParts>(combinedDeviceDossierUrl(kind, productId, deviceClass, 'json', runId));
}

/**
 * Build the URL for the combined EU medicinal CTD/eCTD dossier download endpoint.
 * GET /api/products/{productId}/eu-ctd-dossier?legal_basis=<…>&format=pdf
 *
 * Sibling of {@link combinedEuDossierUrl} (MDR) / {@link combinedIvdrDossierUrl}
 * (IVDR) for drugs + biologics. The discriminator is the Directive 2001/83/EC
 * legal basis (art-8-3 / art-10-1 / art-10-4) rather than a device class.
 */
export function combinedEuCtdDossierUrl(
  productId: number,
  legalBasis: string,
  format: 'pdf' = 'pdf',
): string {
  return `/api/products/${productId}/eu-ctd-dossier?legal_basis=${encodeURIComponent(legalBasis)}&format=${format}`;
}
